import type { FC, ReactNode } from 'react'
import type { ClinicStatus } from '@/lib/clinic-status'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { getClinicStatus } from '@/lib/clinic-status'
import { buttonVariants } from '@/components/ui/button'
import { clinic, sessions } from '@/constants/clinic'

import {
    CalendarOff,
    Clock,
    Navigation,
    PhoneCall,
    Siren,
    Sun,
    Sunset
} from 'lucide-react'

const LiveStatus: FC = (): ReactNode => {
    const [status, setStatus] = useState<ClinicStatus | null>(null)

    useEffect(() => {
        const update = (): void => setStatus(getClinicStatus(new Date()))

        update()
        const timer = window.setInterval(update, 60000)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <div className='flex min-h-8 flex-wrap items-center gap-2' aria-live='polite'>
            {status && (
                <span
                    className={cn(
                        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset',
                        status.open ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-slate-100 text-slate-600 ring-slate-200'
                    )}
                >
                    <span className='relative flex size-2' aria-hidden='true'>
                        {status.open && <span className='absolute inline-flex size-full animate-pulse-ring rounded-full bg-emerald-400 motion-reduce:animate-none' />}
                        <span className={cn('relative inline-flex size-2 rounded-full', status.open ? 'bg-emerald-500' : 'bg-slate-400')} />
                    </span>
                    {status.label}
                </span>
            )}

            <span className='inline-flex items-center rounded-full bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 ring-inset'>
                UGD 24 jam, setiap hari
            </span>
        </div>
    )
}

const sessionBlocks = [
    {
        icon: Sun,
        iconClass: 'text-amber-500',
        title: sessions.pagi.name,
        primary: `Pendaftaran ${sessions.pagi.registration} WIB`,
        secondary: `Praktik dokter mulai ${sessions.pagi.practice} WIB`
    },
    {
        icon: Sunset,
        iconClass: 'text-sky-600',
        title: sessions.sore.name,
        primary: `Pendaftaran ${sessions.sore.registration} WIB`,
        secondary: `Praktik dokter mulai ${sessions.sore.practice} WIB`
    },
    {
        icon: CalendarOff,
        iconClass: 'text-slate-500',
        title: 'Hari Minggu',
        primary: 'Poli rawat jalan tutup',
        secondary: 'UGD dan persalinan tetap buka 24 jam.'
    }
]

const HoursSection: FC = (): ReactNode => {
    return (
        <section id='jam-layanan' aria-labelledby='jam-layanan-title' className='pt-10 pb-4 sm:pt-14'>
            <div className='mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-[1.65fr_1fr] lg:px-8'>
                <div className='rounded-3xl bg-white p-6 shadow-sm ring-1 ring-border sm:p-8'>
                    <div className='flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between'>
                        <div className='flex items-start gap-4'>
                            <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100'>
                                <Clock className='size-6' aria-hidden='true' />
                            </span>

                            <div>
                                <h2 id='jam-layanan-title' className='text-xl font-extrabold tracking-tight text-ink sm:text-2xl'>
                                    Jam layanan
                                </h2>

                                <p className='mt-1 text-sm text-muted-foreground sm:text-[15px]'>
                                    Poli rawat jalan (Umum, Gigi, KIA/KB): Senin sampai Sabtu
                                </p>
                            </div>
                        </div>

                        <LiveStatus />
                    </div>

                    <div className='mt-7 grid gap-3 sm:grid-cols-3'>
                        {sessionBlocks.map(block => {
                            const Icon = block.icon

                            return (
                                <div key={block.title} className='rounded-2xl bg-muted/70 p-5'>
                                    <p className='flex items-center gap-2 text-sm font-bold text-ink'>
                                        <Icon className={cn('size-4', block.iconClass)} aria-hidden='true' />
                                        {block.title}
                                    </p>

                                    <p className='mt-3 text-[15px] font-semibold text-ink tabular-nums'>
                                        {block.primary}
                                    </p>

                                    <p className='mt-1 text-sm text-muted-foreground'>
                                        {block.secondary}
                                    </p>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className='relative isolate overflow-hidden rounded-3xl bg-linear-to-br from-rose-700 to-rose-800 p-6 text-white shadow-xl shadow-rose-900/20 sm:p-8'>
                    <div className='absolute -top-16 -right-16 -z-10 size-56 rounded-full bg-white/10' aria-hidden='true' />
                    <div className='absolute -right-6 -bottom-20 -z-10 size-40 rounded-full bg-white/5' aria-hidden='true' />

                    <span className='flex size-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20'>
                        <Siren className='size-6' aria-hidden='true' />
                    </span>

                    <h2 className='mt-5 text-xl font-extrabold tracking-tight sm:text-2xl'>
                        Keadaan darurat?
                    </h2>

                    <p className='mt-2 leading-relaxed text-white/90'>
                        UGD buka 24 jam setiap hari. Datang langsung atau hubungi
                        {' '}
                        <a href={clinic.phone.href} className='font-bold whitespace-nowrap text-white underline decoration-white/40 underline-offset-4 hover:decoration-white'>
                            {clinic.phone.label}
                        </a>
                        . Tidak perlu membuat janji.
                    </p>

                    <div className='mt-6 flex flex-wrap gap-3'>
                        <a href={clinic.phone.href} className={buttonVariants({ variant: 'inverse' })}>
                            <PhoneCall className='size-4 text-rose-600' aria-hidden='true' />
                            Telepon UGD
                        </a>

                        <a
                            href={clinic.mapsUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(buttonVariants({ variant: 'ghost' }), 'text-white ring-1 ring-white/30 hover:bg-white/10')}
                        >
                            <Navigation className='size-4' aria-hidden='true' />
                            Petunjuk arah
                        </a>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HoursSection
